import {
  useRef,
  useState,
  useCallback,
  FC,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
} from 'react';
import clsx from 'clsx';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { ProductDescType } from 'screens/AccessExpressV3/accessExpress.types';
import { motion } from 'framer-motion';
type AttributeType = {
  id: string;

  element: ReactNode;
};

interface AttributesProps {
  productDesc: ProductDescType;
  attributes: AttributeType[];
  onAddToCart: () => void;
}

const Attributes: FC<AttributesProps> = ({ attributes, onAddToCart }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selected, setSelected] = useState<string[]>([attributes[0]?.id]);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, attributes.length);
  }, [attributes]);

  const handleStep = useCallback(
    (step: number) => {
      const newStep = step + 1;
      if (newStep >= 0 && newStep < attributes.length) {
        const nextId = attributes[newStep]?.id;
        setSelected((prev) => {
          if (!prev.includes(nextId)) return [...prev, nextId];
          return prev;
        });
        setCurrentStep(newStep);
      }
    },
    [attributes],
  );

  const [isScrolling, setIsScrolling] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onScroll = () => {
    if (!isScrolling) {
      setIsScrolling(true);
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 600);
  };

  const [totalHeightAbove, setTotalHeightAbove] = useState(0);

  useEffect(() => {
    setTotalHeightAbove(() => {
      const _totalHeightAbove =
        sectionRefs.current
          .slice(0, currentStep)
          .reduce((sum, ref) => sum + (ref?.offsetHeight || 0), 0) +
        (currentStep > 0 ? 56 : 0) +
        currentStep * 36;
      return _totalHeightAbove;
    });
  }, [currentStep]);

  return (
    <div className="relative w-full rounded bg-transparent p-[1px] lg:w-[330px]">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '1px',
          background:
            'linear-gradient(138.31deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 99.4%)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          WebkitMaskComposite: 'xor',
        }}
      />
      <div
        style={{
          background:
            'linear-gradient(180deg, rgba(25, 25, 37, 0) 63.81%, #191925 124.81%)',
        }}
        className="pointer-events-none absolute left-0 top-0 z-10 h-full w-full rounded shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
      />
      <div className="relative flex h-[calc(100vh-206px)] w-full self-start rounded bg-[#262636] bg-opacity-[54%]  lg:h-[calc(100vh-270px)] xl:h-[calc(100vh-206px)]">
        <div className="flex h-full w-full flex-col overflow-hidden py-8">
          <div className="flex gap-1 px-6">
            {attributes.map((step) => (
              <div
                key={step.id}
                className={clsx(
                  'h-1.5 w-full rounded-sm',
                  selected.includes(step.id)
                    ? 'bg-accent-primary'
                    : 'bg-neutral-700',
                )}
              />
            ))}
          </div>
          <div
            className={clsx(
              '-mr-2 overflow-y-scroll pr-2 scrollbar-thin scrollbar-track-transparent hover:-mr-[0px] hover:pr-[0px]',
              {
                'scrollbar-thumb-stroke-base-subdued': isScrolling,
                'scrollbar-thumb-transparent': !isScrolling,
              },
            )}
            onScroll={onScroll}
            onWheel={(e) => {
              if (e.deltaY < 0) {
                onScroll();
                setCurrentStep((prev) => (prev === 0 ? 0 : prev - 1));
              }
            }}
          >
            <motion.div
              transition={{
                type: 'spring',
                mass: 1,
                stiffness: 60,
                damping: 20.3,
                duration: 0.591,
              }}
              animate={{ y: `-${totalHeightAbove}px` }}
              className="relative flex h-full flex-col gap-9 "
            >
              {attributes.map((att, index) => (
                <div
                  key={att.id}
                  ref={(el) => (sectionRefs.current[index] = el)}
                  className={clsx(
                    'mt-14 w-full flex-shrink-0 xl:min-h-[calc(100%-270px)]',
                    selected.includes(att.id)
                      ? 'relative z-10'
                      : 'pointer-events-none',
                    // index === attributes.length - 1
                    //   ? 'h-full'
                    //   : 'xl:min-h-[calc(100%-212px)]',
                    {
                      'opacity-0 transition-opacity duration-[600ms]':
                        currentStep > index,
                    },
                  )}
                >
                  {cloneElement(
                    isValidElement(att.element) ? (
                      att.element
                    ) : (
                      <>{att.element}</>
                    ),
                    { handleStep, step: index },
                  )}
                  {index === attributes.length - 1 && (
                    <div className="px-6">
                      <GraniteButton
                        onClick={onAddToCart}
                        variant="tertiary"
                        className="mt-8 w-full"
                      >
                        Add to cart
                      </GraniteButton>
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attributes;
